import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { blog } from "../lib/api";
import { ArrowRight } from "lucide-react";

/**
 * Compact SEO-rich blog strip that lives at the bottom of the home page.
 * Surfaces the latest 3 published posts to help search engines crawl them.
 */
export default function BlogHomeStrip() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    blog.list({ limit: 3 }).then(setPosts).catch(() => {});
  }, []);

  if (posts.length === 0) return null;

  return (
    <section data-testid="blog-home-strip" className="relative bg-cream py-24 md:py-32 border-t border-black/8">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <span className="eyebrow">— The Onyx Journal</span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 text-ink leading-tight">DC nightlife, decoded.</h2>
            <p className="text-muted max-w-xl mt-4">Guides, stories, and insider intel on Washington DC's best rooftop nightlife — straight from the team at Onyx.</p>
          </div>
          <Link to="/blog" data-testid="blog-strip-all" className="btn-luxe btn-luxe-ghost self-start md:self-auto">Read All Articles</Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {posts.map((p) => (
            <Link
              key={p.id}
              to={`/blog/${p.slug}`}
              data-testid={`blog-strip-${p.slug}`}
              className="group bg-white border border-black/10 hover:border-ink transition-all duration-300 hover:-translate-y-1 flex flex-col"
            >
              <div className="aspect-[16/10] overflow-hidden bg-black">
                {p.cover_image_url ? (
                  <img
                    src={p.cover_image_url}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                ) : null}
              </div>
              <div className="p-6 flex flex-col flex-1">
                <p className="text-[10px] tracking-luxe uppercase text-gold mb-2">{p.category} · {p.reading_minutes} min read</p>
                <h3 className="font-display text-xl md:text-2xl text-ink leading-tight mb-3 line-clamp-2">{p.title}</h3>
                <p className="text-sm text-muted leading-relaxed line-clamp-3 mb-5">{p.excerpt}</p>
                <span className="mt-auto inline-flex items-center gap-2 text-[10px] tracking-luxe uppercase text-ink group-hover:text-gold transition">
                  Read More <ArrowRight size={12} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
